import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API } from "../lib/api";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";

const usd = (c) => (c == null ? "" : `$${(c / 100).toFixed(2)}`);
const origin = () => window.location.origin;

export default function Checkout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const slug = params.get("product") || "braced-no-more-book";
  const upgrade = params.get("upgrade"); // "book" pre-selects the book from the reader paywall
  const bumpParam = params.get("bump"); // "1" pre-checks the Vault order-bump (Integrated Sanctuary)
  const applied = params.get("applied"); // set after the immersion intake is completed
  const [product, setProduct] = useState(null);
  const [vault, setVault] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bump, setBump] = useState(bumpParam === "1");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [plan, setPlan] = useState("monthly"); // immersion only: monthly | full

  useEffect(() => {
    const target = upgrade === "book" ? "braced-no-more-book" : slug;
    Promise.all([
      axios.get(`${API}/products/${target}`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/products/the-language-vault`).then((r) => r.data).catch(() => null),
    ]).then(([prod, v]) => {
      // Application-only products have no checkout — send them to the detail page.
      if (prod && (prod.is_application || !prod.offer)) { navigate(`/offerings/${prod.slug}`, { replace: true }); return; }
      // The Immersion requires completing the intake first; bounce direct visits back to it.
      if (prod && prod.offer === "immersion" && !applied) { navigate(`/offerings/${prod.slug}`, { replace: true }); return; }
      setProduct(prod); setVault(v);
    }).finally(() => setLoading(false));
  }, [slug, upgrade, applied, navigate]);

  // The Vault order-bump is only offered when buying the one-time book.
  const canBump = product && product.slug === "braced-no-more-book";

  const offer = useMemo(() => {
    if (!product) return null;
    if (product.slug === "braced-no-more-book") return bump ? "bundle" : "book";
    if (product.offer === "immersion") return plan === "full" ? "immersion_full" : "immersion";
    return product.offer; // vault | orientation
  }, [product, bump, plan]);

  const isImmersion = product && product.offer === "immersion";
  const immersionMonthly = (product && product.price_cents) || 80000;
  const immersionMonths = (product && product.duration_months) || 8;
  const immersionFull = immersionMonthly * immersionMonths;

  const summary = useMemo(() => {
    if (!product) return [];
    const rows = [];
    if (product.slug === "braced-no-more-book") {
      rows.push({ label: product.title, amount: usd(product.price_cents), sub: "one-time" });
      if (bump && vault) rows.push({ label: "The Language Vault", amount: usd(vault.intro_price_cents || vault.price_cents), sub: "first month, then $25/mo · cancel anytime" });
    } else if (product.offer === "immersion") {
      if (plan === "full") {
        rows.push({ label: product.title, amount: usd(immersionFull), sub: `paid in full · ${immersionMonths}-month program` });
      } else {
        rows.push({ label: product.title, amount: usd(immersionMonthly), sub: `${usd(immersionMonthly)}/month × ${immersionMonths} — auto-completes after the final payment` });
      }
    } else if (product.is_recurring) {
      const hasIntro = product.intro_price_cents && product.intro_price_cents !== (product.standard_price_cents || product.price_cents);
      rows.push({ label: product.title,
        amount: usd(hasIntro ? product.intro_price_cents : (product.standard_price_cents || product.price_cents)),
        sub: hasIntro
          ? `first month, then ${usd(product.standard_price_cents || product.price_cents)}/mo`
          : `${usd(product.standard_price_cents || product.price_cents)}/month${product.duration_months ? ` · ${product.duration_months} months` : ""}` });
    } else {
      rows.push({ label: product.title, amount: usd(product.price_cents), sub: "one-time" });
    }
    return rows;
  }, [product, bump, vault, plan, immersionFull, immersionMonthly, immersionMonths]);

  const dueToday = useMemo(() => {
    if (!product) return 0;
    if (product.slug === "braced-no-more-book") return product.price_cents + (bump && vault ? (vault.intro_price_cents || vault.price_cents) : 0);
    if (product.offer === "immersion") return plan === "full" ? immersionFull : immersionMonthly;
    if (product.is_recurring) return product.intro_price_cents || product.price_cents;
    return product.price_cents;
  }, [product, bump, vault, plan, immersionFull, immersionMonthly]);

  const checkout = async () => {
    setSubmitting(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, { offer, origin_url: origin() });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e.response?.data?.detail || "We couldn't open checkout just now. Please try again in a moment.");
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex min-h-[100dvh] items-center justify-center watercolor-wash"><Loader2 className="h-6 w-6 animate-spin text-ink-muted" /></div>;
  if (!product) return (
    <div className="flex min-h-[100dvh] items-center justify-center watercolor-wash px-5 text-center">
      <div><p className="text-ink-soft">We couldn't find that offering.</p><Link to="/offerings" className="mt-3 inline-block text-terracotta underline">See all offerings</Link></div>
    </div>
  );

  return (
    <div className="min-h-[100dvh]" style={{ background: "linear-gradient(180deg,#F3EFEA 0%,#ECE7DE 100%)" }} data-testid="checkout-page">
      <div className="mx-auto max-w-xl px-5 py-12 sm:px-8">
        <Link to={`/offerings/${product.slug}`} data-testid="checkout-back" className="mb-8 inline-flex items-center gap-2 font-display text-sm text-ink-soft hover:text-terracotta">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <p className="kicker text-ink-muted">A quiet checkout</p>
        <h1 className="mt-2 font-display text-4xl text-ink sm:text-5xl">Carry it forward.</h1>

        {/* Immersion payment plan: spread over 8 months, or pay in full */}
        {isImmersion && (
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="immersion-plan">
            <button type="button" onClick={() => setPlan("monthly")} data-testid="immersion-plan-monthly"
              className={`rounded-2xl border-2 p-5 text-left transition ${plan === "monthly" ? "border-gold bg-gold/10" : "border-ink/15 bg-ivory-50 hover:border-gold/40"}`}>
              <p className="font-display text-ink">Over 8 months</p>
              <p className="mt-1 text-sm text-ink-soft">{usd(immersionMonthly)}/month × {immersionMonths}</p>
              <p className="mt-2 text-[12px] leading-relaxed text-ink-muted">Begin today with your first payment; the rest bill monthly on this date and stop automatically after the 8th.</p>
            </button>
            <button type="button" onClick={() => setPlan("full")} data-testid="immersion-plan-full"
              className={`rounded-2xl border-2 p-5 text-left transition ${plan === "full" ? "border-gold bg-gold/10" : "border-ink/15 bg-ivory-50 hover:border-gold/40"}`}>
              <p className="font-display text-ink">Pay in full</p>
              <p className="mt-1 text-sm text-ink-soft">{usd(immersionFull)} once</p>
              <p className="mt-2 text-[12px] leading-relaxed text-ink-muted">One payment for the whole {immersionMonths}-month journey — nothing further to manage.</p>
            </button>
          </div>
        )}

        {/* Order-bump */}
        {canBump && vault && (
          <button
            type="button"
            onClick={() => setBump((b) => !b)}
            data-testid="vault-order-bump"
            className={`mt-8 block w-full rounded-2xl border-2 border-dashed p-5 text-left transition ${bump ? "border-sage bg-sage/10" : "border-ink/20 bg-ivory-50 hover:border-terracotta/40"}`}
          >
            <div className="flex items-start gap-3">
              <span className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded border ${bump ? "border-sage bg-sage text-ivory-50" : "border-ink/30"}`}>{bump ? "✓" : ""}</span>
              <div>
                <p className="font-display text-ink">Add The Language Vault to your journey</p>
                <p className="mt-1 text-sm text-ink-soft">Small, steady support: $15 your first month, then $25/month. Cancel anytime.</p>
                <p className="mt-2 text-[13px] italic leading-relaxed text-ink-muted">"Bracing does not unlearn itself overnight. Keep gentle somatic reframes, weekly audio notes from Casey, and restorative language close while you move through the chapters."</p>
              </div>
            </div>
          </button>
        )}

        {/* Summary */}
        <div className="mt-8 rounded-2xl bg-ivory-50 p-6 shadow-sm ring-1 ring-ivory-400/60" data-testid="checkout-summary">
          {summary.map((r, i) => (
            <div key={i} className="flex items-start justify-between border-b border-ivory-400/50 py-3 first:pt-0 last:border-0">
              <div><p className="font-display text-ink">{r.label}</p><p className="text-xs text-ink-muted">{r.sub}</p></div>
              <p className="font-display text-ink">{r.amount}</p>
            </div>
          ))}
          <div className="mt-3 flex items-center justify-between">
            <p className="font-display text-ink">Due today</p>
            <p className="font-display text-xl text-ink" data-testid="checkout-total">{usd(dueToday)}</p>
          </div>
        </div>

        {err && <p className="mt-3 text-sm text-terracotta" data-testid="checkout-error">{err}</p>}

        <button type="button" onClick={checkout} disabled={submitting} data-testid="checkout-submit" className="debossed-btn mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-display text-ink disabled:opacity-50">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Continue to secure payment
        </button>
        <p className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs text-ink-muted"><ShieldCheck className="h-3.5 w-3.5" /> Payments handled securely by Stripe</p>
      </div>
    </div>
  );
}
